import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  Alert,
  Stack,
} from "@mui/material";

interface RegistrarBoletoResponseSuccess {
  Numero: string;
  NumeroCarteira: number;
  NumeroVariacaoCarteira: number;
  CodigoCliente: number;
  LinhaDigitavel: string;
  CodigoBarraNumerico: string;
  NumeroContratoCobranca: number;
  UrlImagemBoleto: string;
  Observacao: string;
  Beneficiario: {
    Agencia: number;
    ContaCorrente: number;
    TipoEndereco: number;
    Logradouro: string;
    Bairro: string;
    Cidade: string;
    CodigoCidade: number;
    Uf: string;
    Cep: number;
    IndicadorComprovacao: string;
  };
  QrCode: {
    Url: string;
    TxId: string;
    Emv: string;
  };
}

interface RegistrarBoletoResponseError {
  Erros: {
    Codigo: string;
    Versao: string;
    Mensagem: string;
    Ocorrencia: string;
  }[];
  RawResponse: string;
  StatusCode: number;
}

type BoletoProcessResult =
  | RegistrarBoletoResponseSuccess
  | RegistrarBoletoResponseError;

const isErrorResponse = (
  item: BoletoProcessResult
): item is RegistrarBoletoResponseError => {
  return "Erros" in item;
};

// O endpoint devolve List<dynamic>: alem dos dois objetos acima, entra TEXTO quando
// a parcela foi pulada ("Empty Boleto CreID: 127", "Internal Exception ..."). O tipo
// precisa dizer isso, senao o componente nao consegue nem olhar para esse caso.
type BoletoProcessItem = BoletoProcessResult | string;

interface ProcessedBoletosDialogProps {
  open: boolean;
  results: BoletoProcessItem[];
  onOk: () => void;
  onReprocessar?: () => void;
  reprocessing?: boolean;
}

/**
 * Poe QUALQUER resposta de geracao de boleto no formato que este dialogo espera.
 *
 * [23/10/2026 - card VENDAS BUG TELA EM BRANCO] Existe porque a resposta nem sempre e
 * uma lista. Quando a chamada ao banco FALHA, o BoletoController devolve `Data` como
 * um TEXTO ("Erro 500: Erro interno do servidor."). Esse texto chegava aqui e o
 * `results.filter(...)` abaixo estourava - texto nao tem `.filter` -, o React desmontava
 * a aplicacao inteira e o usuario via a tela branca de 13/10, com a venda JA GRAVADA.
 *
 * O texto NAO e descartado: vira um item da lista e aparece como "nao processada", que
 * e a verdade - houve uma tentativa e ela falhou, com motivo. Descartar seria repetir o
 * defeito de 07/08, quando o dialogo mostrava "0 sucesso / 0 erro" e corpo vazio, que o
 * usuario lia como sucesso.
 */
export const normalizarResultadoBoletos = (dados: any): BoletoProcessItem[] => {
  if (Array.isArray(dados)) return dados;
  if (dados === null || dados === undefined) return [];
  return [dados];
};

export const ProcessedBoletosDialog: React.FC<
  ProcessedBoletosDialogProps
> = ({ open, results, onOk, onReprocessar, reprocessing }) => {
  /* A normalizacao acontece AQUI TAMBEM, e nao so em quem chama, de proposito: sao
     dois chamadores (salvar o pedido e reprocessar), e o dia em que surgir um terceiro
     ninguem vai lembrar desta regra. O componente que quebra e o que tem de se
     defender. */
  const lista = normalizarResultadoBoletos(results);

  // O backend devolve TRES coisas nesta lista: objeto de sucesso, objeto de erro do
  // BB, e TEXTO quando a parcela foi pulada ("Empty Boleto CreID: 127",
  // "Internal Exception ..."). Antes o texto era descartado - e com isso um
  // reprocessamento em que NENHUM boleto saiu aparecia como "0 sucesso / 0 erro" e
  // corpo vazio, que o usuario le como sucesso (chamado de 07/08/2026). Parcela
  // pulada e falha: tem de aparecer.
  const items = lista.filter(
    (x): x is BoletoProcessResult => !!x && typeof x === "object"
  );

  const puladas = lista.filter(
    (x): x is string => typeof x === "string" && x.trim().length > 0
  );

  const successCount = items.filter((x) => !isErrorResponse(x)).length;

  const errorCount = items.filter((x) => isErrorResponse(x)).length;

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Processamento de Boletos
      </DialogTitle>

      <DialogContent>
        <Stack
          direction="row"
          spacing={1}
          mb={3}
        >
          <Chip
            color="success"
            label={`${successCount} sucesso(s)`}
          />

          <Chip
            color="error"
            label={`${errorCount} erro(s)`}
          />

          {puladas.length > 0 && (
            <Chip
              color="warning"
              label={`${puladas.length} nao processada(s)`}
            />
          )}
        </Stack>

        {puladas.length > 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
          >
            <Typography fontWeight={600}>
              Parcela(s) que o sistema nao chegou a enviar ao banco
            </Typography>

            <Typography variant="body2" mb={1}>
              Nenhum boleto foi gerado para elas. A parcela continua como esta e
              nao ha segunda via a emitir.
            </Typography>

            {puladas.map((motivo, index) => (
              <Typography
                key={index}
                variant="body2"
              >
                {motivo}
              </Typography>
            ))}
          </Alert>
        )}

        <Stack spacing={2}>
          {items.map((item, index) => {
            if (isErrorResponse(item)) {
              return (
                <Alert
                  key={index}
                  severity="error"
                >
                  <Typography fontWeight={600}>
                    Boleto não registrado
                  </Typography>

                  <Typography variant="body2">
                    HTTP Status: {item.StatusCode}
                  </Typography>

                  {item.Erros.map((erro) => (
                    <Box
                      key={`${erro.Codigo}-${erro.Ocorrencia}`}
                      mt={1}
                    >
                      <Typography>
                        <strong>Código:</strong> {erro.Codigo}
                      </Typography>

                      <Typography>
                        <strong>Mensagem:</strong>{" "}
                        {erro.Mensagem}
                      </Typography>

                      <Typography>
                        <strong>Ocorrência:</strong>{" "}
                        {erro.Ocorrencia}
                      </Typography>
                    </Box>
                  ))}
                </Alert>
              );
            }

            return (
              <Box
                key={index}
                border="1px solid"
                borderColor="success.light"
                borderRadius={2}
                p={2}
              >
                <Typography
                  fontWeight={600}
                  color="success.main"
                >
                  Boleto registrado com sucesso
                </Typography>

                <Divider sx={{ my: 1 }} />

                <Typography>
                  <strong>Número:</strong> {item.Numero}
                </Typography>

                <Typography>
                  <strong>Linha Digitável:</strong>{" "}
                  {item.LinhaDigitavel}
                </Typography>

                <Typography>
                  <strong>Código Cliente:</strong>{" "}
                  {item.CodigoCliente}
                </Typography>

                {item.Observacao && (
                  <Typography>
                    <strong>Observação:</strong>{" "}
                    {item.Observacao}
                  </Typography>
                )}

                {item.UrlImagemBoleto && (
                  <Typography>
                    <strong>Boleto:</strong>{" "}
                    <a
                      href={item.UrlImagemBoleto}
                      target="_blank">
                      Visualizar
                    </a>
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions>
        {errorCount > 0 && onReprocessar && (
          <Button
            variant="outlined"
            color="warning"
            onClick={onReprocessar}
            disabled={reprocessing}
          >
            {reprocessing ? "Reprocessando..." : "Reprocessar"}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={onOk}
          disabled={reprocessing}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};
